import React, {useState} from 'react';
import { useNavigate} from "react-router-dom";
import {
    Button, IconButton, Container, Stack, Box, useMediaQuery, Grid, Menu, MenuItem, ListItemIcon, ListItemText
} from "@mui/material";
import HomeIcon from '@mui/icons-material/Home';
import CheckIcon from '@mui/icons-material/Check';
import WorkIcon from '@mui/icons-material/Work';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import TranslateIcon from '@mui/icons-material/Translate';
import HardwareIcon from '@mui/icons-material/Hardware';
import InfoIcon from '@mui/icons-material/Info';
import {useTheme} from "@mui/material/styles";
import WordDeleteView from "../../vocabulary/components/WordDeleteView/WordDeleteView";
import LogoErrorView from "../../../common/components/LogoErrorView";
import WordUpdateView from "../../vocabulary/components/WordUpdateView/WordUpdateView";
import useLesson from "../hooks/useLesson";
import DeckFinishedView from "../components/DeckFinishedView";
import WordSummaryView from "../components/WordSummaryView";
import StudySessionView from "../components/StudySessionView";
import ContinueLessonView from "../components/ContinueLessonView";
import {useAuth} from "../../auth/context/AuthContext";


function Lesson() {

    const navigate = useNavigate();
    const theme = useTheme();

    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const {
        isLessonReady,
        isLoadError,
        isOutOfSyncError,
        currentWord,
        currentWordIndex,
        currentBatch,
        remainingWordsInCurrentBatch,
        allWordsInDeckFinished,
        isReadyForQuiz,
        isQuizActive,
        startQuiz,
        remainingQuizItems,
        completedWords,
        next,
        prev,
        onItemPassedQuiz,
        displayContinueLessonDialog,
        nextBatch,
        onCurrentLessonDeleted,
        onCurrentWordUpdated,
        playWordAudio,
        playSentenceAudio,
        cleanAudioFiles,
    } = useLesson(5);

    const { isAdmin } = useAuth();

    const [direction, setDirection] = useState<1 | -1>(1);
    const [displayUpdateWordDrawer, setDisplayUpdateWordDrawer] = useState<boolean>(false);
    const [displayDeleteWordDrawer, setDisplayDeleteWordDrawer] = useState<boolean>(false);
    const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState<null | HTMLElement>(null);

    const handleNext = () => {
        setDirection(1);
        next();
    }

    const handlePrev = () => {
        setDirection(-1);
        prev();
    }

    const finishLessons = () => {
        cleanAudioFiles();
        navigate('/', {replace: true});
    };


    document.onkeydown = function (e) {
        if (!isQuizActive && !displayUpdateWordDrawer && !displayDeleteWordDrawer && !displayContinueLessonDialog) {
            if (e.key === 'ArrowLeft')
                handlePrev();
            else if (e.key === 'ArrowRight')
                handleNext();
            else if (e.key === 'Enter')
                startQuiz();
            else if (e.key === 'v')
                playWordAudio();
            else if (e.key === 'b')
                playSentenceAudio();
        }
    };

    if (isLoadError) {
        return (
            <LogoErrorView severity={"error"} message={`Could not load lessons. Try refreshing the page.`} />
        )
    }

    if (isOutOfSyncError) {
        return (
            <LogoErrorView severity={"error"} message={`Session desynced. Please refresh the page.`} />
        )
    }

    if (allWordsInDeckFinished) {
        return (
            <DeckFinishedView onContinue={ finishLessons} />
        )
    }

    if (isQuizActive) {
        return (
            <>
                <StudySessionView items={remainingQuizItems}
                                  words={currentBatch!.map(word => ({...word, synonyms: [], homonymTranslations: []}))}
                                  onItemReviewed={onItemPassedQuiz}/>
                {topLeftActionRowView()}
            </>
        )
    }

    return (
        <Container disableGutters
                   maxWidth={false}
                   sx={{height: '100vh', display: "flex", flexDirection: "column", alignItems: "center", position: "relative", overflow: "hidden", bgcolor: theme.palette.background.default}}>
            {topSpacerView()}
            {lessonItemView()}
            {navigationButtonsBarView()}
            {topLeftActionRowView()}
            {topRightActionRowView()}
            {nextBatchView()}
            {editDrawerView()}
            {deleteDrawerView()}
        </Container>
    );

    function topSpacerView() {
        return (
            <Box sx={{flex: "0 0 auto", width: "100%", height: {xs: 56, sm: 48}}} />
        )
    }

    function lessonItemView() {
        return !displayContinueLessonDialog && (
            <Box sx={{flex: "1 1 auto", minHeight: 0, width: "100%", overflowY: "auto", display: "flex", justifyContent: "center"}}>
                <Box sx={{mt: 2, maxWidth: isMobile ? '100%' : 700, minWidth: isMobile ? '100%' : 700, width: "100%", px: 2}}>
                    <WordSummaryView word={currentWord}
                                     displayOptions={{
                                         lemmaHeader: {order: 1, title: "New word", icon: LightbulbIcon, bgcolor: theme.palette.primary.light, color: theme.palette.primary.contrastText, showImage: true, showPos: true},
                                         translation: {order: 2, title: "Translation", icon: TranslateIcon, addMarginAbove: true, bgcolor: theme.palette.primary.main, color: theme.palette.primary.contrastText},
                                         exampleSentence: {order: 3, title: "Example sentence", icon: InfoIcon, addMarginAbove: true, bgcolor: theme.palette.primary.light, color: theme.palette.primary.contrastText},
                                         conjugation: {order: 4, title: "Conjugation", icon: HardwareIcon, addMarginAbove: true, bgcolor: theme.palette.primary.main, color: theme.palette.primary.contrastText}
                                     }}
                                     animationDirection={direction}/>
                </Box>
            </Box>
        )
    }

    function navigationButtonsBarView() {
        return !displayContinueLessonDialog && (
            <Box sx={{flex: "0 0 auto", width: "100%", display: "flex", justifyContent: "center"}}>
                <Box sx={{maxWidth: isMobile ? '100%' : 700, minWidth: isMobile ? '100%' : 700, width: "100%", px: 2, pt: 1, pb: 5}}>
                    {navigationButtonsView()}
                </Box>
            </Box>
        )
    }

    function navigationButtonsView() {
        if (isQuizActive)
            return;

        const buttonGenerator = (text: string, title: string, bgcolor: string, color: string, isDisabled: boolean, onClick: () => void, startIcon?: React.ReactNode, endIcon?: React.ReactNode) => (
            <Button
                variant="contained"
                fullWidth
                startIcon={startIcon}
                endIcon={endIcon}
                sx={{
                    borderRadius: 3,
                    color: "white",
                    backgroundColor: bgcolor,
                    fontFamily: "bold",
                    // "&:hover": {
                    //     backgroundColor: theme.palette[color].dark,
                    // },
                }}
                onClick={onClick}
                title={title}
                disabled={isDisabled}
            >
                {text}
            </Button>

        )

        const prevButton = buttonGenerator("Previous", "Previous word (left arrow)", theme.palette.secondary.dark, theme.palette.secondary.contrastText, !isLessonReady || currentWordIndex === 0, handlePrev, <ArrowBackIcon/>);
        const nextButton = buttonGenerator("Next", "Next word (right arrow)", theme.palette.secondary.dark, theme.palette.secondary.contrastText, !isLessonReady || currentWordIndex === currentBatch!.length - 1, handleNext, undefined, <ArrowForwardIcon/>);
        const quizButton = buttonGenerator("Start quiz", "Enter", theme.palette.success.dark, theme.palette.success.contrastText, !isReadyForQuiz, startQuiz);

        if (isMobile) {
            return (
                <Grid container spacing={2} sx={{mt: 3}}>
                    <Grid item xs={6}>{prevButton}</Grid>
                    <Grid item xs={6}>{nextButton}</Grid>
                    <Grid item xs={12}>{quizButton}</Grid>
                </Grid>
            )
        }

        return (
            <Grid container spacing={2} sx={{mt: 2}}>
                <Grid item xs={4}>{prevButton}</Grid>
                <Grid item xs={4}>{quizButton}</Grid>
                <Grid item xs={4}>{nextButton}</Grid>
            </Grid>
        )
    }

    function topLeftActionRowView() {
        return (
            <Stack direction="row" alignItems="center" spacing={2} sx={{position: "absolute", top: 3, left: 1}}>
                <IconButton title="Finish lessons" color={"inherit"} onClick={() => completedWords.length ? finishLessons() : navigate('/')}>
                    <HomeIcon/>
                </IconButton>
                <div title="Completed lessons">
                    <CheckIcon/>
                    <span>{completedWords.length}</span>
                </div>
                <div title="Batch progress">
                    <WorkIcon/>
                    <span>{isQuizActive ? remainingWordsInCurrentBatch : !isLessonReady ? 0 : `${(currentWordIndex + 1)}/${currentBatch!.length}`}</span>
                </div>
            </Stack>
        )
    }

    function topRightActionRowView() {
        return !isQuizActive && !displayContinueLessonDialog && (
            <Stack direction="row" alignItems="center" spacing={2} sx={{position: "absolute", top: 3, right: 1}}>
                <IconButton title="Menu" onClick={(e) => setActionMenuAnchorEl(e.currentTarget)} sx={{color: "black"}}>
                    <MenuIcon/>
                </IconButton>
                <Menu anchorEl={actionMenuAnchorEl} open={Boolean(actionMenuAnchorEl)} onClose={() => setActionMenuAnchorEl(null)}>
                    {isAdmin && (
                        <MenuItem disabled={!isLessonReady} onClick={() => {setActionMenuAnchorEl(null); setDisplayUpdateWordDrawer(true);}}>
                            <ListItemIcon><EditIcon fontSize="small" sx={{color: "black"}}/></ListItemIcon>
                            <ListItemText>Edit word</ListItemText>
                        </MenuItem>
                    )}
                    <MenuItem disabled={!isLessonReady} onClick={() => {setActionMenuAnchorEl(null); setDisplayDeleteWordDrawer(true);}}>
                        <ListItemIcon><DeleteIcon fontSize="small" sx={{color: "black"}}/></ListItemIcon>
                        <ListItemText>Delete word</ListItemText>
                    </MenuItem>
                </Menu>
            </Stack>
        )
    }

    function nextBatchView() {
        return displayContinueLessonDialog && (
            <Box sx={{position: "absolute", inset: 0, bgcolor: theme.palette.background.default, zIndex: (t) => t.zIndex.modal}}>
                <ContinueLessonView onContinueLessons={nextBatch} onFinishLessons={ finishLessons } />
            </Box>
        )
    }

    function editDrawerView() {
        return displayUpdateWordDrawer && (
            <WordUpdateView word={currentWord!}
                            actionDispatch={{
                                update: (updatedWord, updatedWordPromise) => {
                                    setDisplayUpdateWordDrawer(false);
                                    onCurrentWordUpdated(updatedWord, updatedWordPromise);
                                }
                            }}
                            onDrawerClosed={() => setDisplayUpdateWordDrawer(false)}
                            snackbar={{showRequestErrorSnackbar: true}}
            />
        )
    }

    function deleteDrawerView() {
        return displayDeleteWordDrawer && (
            <WordDeleteView word={currentWord!}
                            actionDispatch={{
                                delete: () => {
                                    setDisplayDeleteWordDrawer(false);
                                    onCurrentLessonDeleted();
                                }
                            }}
                            onDrawerClosed={() => setDisplayDeleteWordDrawer(false)}
                            snackbar={{showRequestErrorSnackbar: true}}
            />
        )
    }



}

export default Lesson;
